import { useState, useEffect, useCallback, type RefObject } from 'react'

interface UseBackgroundContrastOptions {
  containerRef?: RefObject<HTMLElement | null>
}

/**
 * Detects whether the background behind a given element is dark or light.
 * Hides all fixed/sticky overlays to sample the actual page content behind.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
  options: UseBackgroundContrastOptions = {}
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  const check = useCallback(() => {
    const element = elementRef.current
    if (!element) return

    const rect = element.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top + rect.height / 2

    // Hide all fixed/sticky positioned elements to find the real page content
    const hiddenElements: { el: HTMLElement; prev: string }[] = []
    document.querySelectorAll('*').forEach((el) => {
      if (!(el instanceof HTMLElement)) return
      const pos = getComputedStyle(el).position
      if (pos === 'fixed' || pos === 'sticky') {
        hiddenElements.push({ el, prev: el.style.visibility })
        el.style.visibility = 'hidden'
      }
    })

    const behind = document.elementFromPoint(x, y)

    // Restore all hidden elements
    hiddenElements.forEach(({ el, prev }) => {
      el.style.visibility = prev
    })

    if (!behind) return

    // Walk up the DOM to find the first element with a non-transparent background
    let target: Element | null = behind
    let bg = ''
    while (target && target !== document.documentElement) {
      const style = getComputedStyle(target)
      const color = style.backgroundColor
      if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
        bg = color
        break
      }
      target = target.parentElement
    }

    // Fallback to white if no opaque background found
    if (!bg) bg = 'rgb(255, 255, 255)'

    const match = bg.match(/\d+/g)
    if (match && match.length >= 3) {
      const [r, g, b] = match.map(Number)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      setBackground(luminance < 0.5 ? 'dark' : 'light')
    }
  }, [elementRef, options.containerRef])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // Run checks after progressively longer delays to catch late-rendering content
    const timers = [100, 500, 1500].map((delay) =>
      setTimeout(check, delay)
    )

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    return () => {
      timers.forEach(clearTimeout)
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [elementRef, check])

  return background
}
